import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "phosphor-react";
import { updateSeoMetadata } from "../lib/seo";

export default function Privacy() {
  const navigate = useNavigate();

  useEffect(() => {
    updateSeoMetadata({
      title: "Privacy Policy | GENI",
      description: "Read the Privacy Policy for GENI, the trading analytics and journaling platform.",
      path: "/privacy",
      lang: "en",
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6 md:p-12" data-testid="privacy-page">
      <div className="w-full max-w-[680px] mx-auto">
        
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 group"
          data-testid="privacy-back-button"
        >
          <ArrowLeft size={20} weight="bold" className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>

        {/* Header */}
        <header className="mb-12">
          <h1 className="text-4xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-white/40 text-sm">Last updated: March 2026</p>
        </header>

        {/* Content */}
        <div className="space-y-8 text-white/70 leading-relaxed">
          
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Information We Collect</h2>
            <p className="mb-4">We collect the following types of information:</p>
            
            <h3 className="text-base font-semibold text-white/90 mb-2">Account Information</h3>
            <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
              <li>Email address</li>
              <li>Authentication credentials (securely processed)</li>
            </ul>

            <h3 className="text-base font-semibold text-white/90 mb-2">Trading Data</h3>
            <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
              <li>Trade records</li>
              <li>Chart images</li>
              <li>Notes and annotations</li>
              <li>Performance-related inputs provided by the user</li>
            </ul>

            <p className="mb-2">GENI does NOT require or collect sensitive financial information such as:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
              <li>Total account balance</li>
              <li>Capital size</li>
              <li>Position sizing</li>
            </ul>

            <h3 className="text-base font-semibold text-white/90 mb-2">Usage Data</h3>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Feature interactions</li>
              <li>App performance metrics</li>
              <li>General usage behavior</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. How We Use Your Information</h2>
            <p className="mb-3">Your information is used to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Provide and operate the Service</li>
              <li>Process and analyze your trading data</li>
              <li>Generate data-driven insights and analytics</li>
              <li>Improve product performance and user experience</li>
              <li>Communicate important service updates</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Image Processing (AI Use)</h2>
            <p className="mb-3">When you upload chart images:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
              <li>Images may be processed by artificial intelligence tools to extract structured trade data</li>
              <li>This processing is used solely to assist data entry and improve usability</li>
              <li>Results may be inaccurate or incomplete</li>
            </ul>
            <p className="mb-2">GENI does NOT:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Use your data to train AI models</li>
              <li>Sell or share your data externally</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Data Storage & Security</h2>
            <p className="mb-3">
              We use industry-standard security practices to protect your data.
            </p>
            <p className="mb-2">Infrastructure may include:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
              <li>Secure authentication providers</li>
              <li>Encrypted databases</li>
              <li>Protected cloud storage</li>
            </ul>
            <p>
              We take reasonable measures to prevent unauthorized access, disclosure, or loss of data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Data Sharing</h2>
            <p className="mb-3">We do NOT sell your personal data.</p>
            <p className="mb-2">We may share data only:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>With essential service providers required to operate the Service (e.g., hosting, authentication, processing tools)</li>
              <li>When required by law or legal process</li>
              <li>To protect the rights, safety, or integrity of the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Data Retention</h2>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Your data is retained while your account is active</li>
              <li>Upon account deletion, personal data is removed within 30 days</li>
              <li>Anonymous or aggregated data may be retained for analytics purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Your Rights</h2>
            <p className="mb-2">You have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Access your personal data</li>
              <li>Request corrections</li>
              <li>Delete your account and data</li>
              <li>Export your data</li>
              <li>Opt out of non-essential communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Cookies & Local Storage</h2>
            <p className="mb-2">GENI uses:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
              <li>Local storage for session management and preferences</li>
              <li>Essential cookies required for authentication and core functionality</li>
            </ul>
            <p>We do NOT use third-party tracking cookies.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. International Data Processing</h2>
            <p className="mb-2">
              Your data may be processed in different locations depending on infrastructure providers.
            </p>
            <p>
              We apply reasonable safeguards to protect your data regardless of location.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Changes to This Policy</h2>
            <p className="mb-2">
              We may update this Privacy Policy from time to time.
            </p>
            <p>
              Continued use of the Service after changes constitutes acceptance of the updated Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Contact & Support</h2>
            <p className="mb-2">
              If you have questions about this Privacy Policy or wish to exercise your rights:
            </p>
            <p>
              Please use the <strong className="text-white">Support</strong> section available in your 
              account menu within the application.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
